import { useEffect } from 'react';
import { Controller, useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEntitlements } from '@/hooks/use-entitlements';
import { useRole } from '@/hooks/use-role';
import { getJob, jobQueryKey, type UpdateJobRequest } from '@/lib/api/jobs';
import { listPaymentMethods, paymentMethodsQueryKey } from '@/lib/api/payment-methods';

import {
  availablePaymentMethodOptions,
  formatDate,
  fromJobRecurrence,
  paymentMethodOptionValue,
  toJobRecurrence,
} from './helpers';
import { RecurrenceFields } from './recurrence-fields';
import { RecurrencePreview } from './recurrence-preview';
import {
  editJobSchema,
  type EditJobInput,
  type RecurrenceFormValues,
} from './schema';

interface Props {
  businessId: string;
  jobId: string;
  onSave: (body: UpdateJobRequest) => void;
  onClose: () => void;
  isSaving: boolean;
}

const DEFAULT_VALUES: EditJobInput = {
  title: '',
  serviceAddress: '',
  defaultPaymentMethod: '',
  repeats: false,
  recurrence: {
    frequency: 'weekly',
    intervalCount: '1',
    daysOfWeek: [],
    dayOfMonth: '',
    customRule: '',
    seriesEndDate: '',
  },
};

// Quick-edit dialog for the logical job — title, service address, and the
// recurrence rule. Opened from the job detail page. Execution / payment /
// crew are edited per-visit on the page itself, not here.
export function JobDetailModal({ businessId, jobId, onSave, onClose, isSaving }: Props) {
  const { t } = useLanguage();
  const { has } = useEntitlements();
  const { isOwner } = useRole();
  const canRecur = has('recurring_jobs');

  const jobQuery = useQuery({
    queryKey: jobQueryKey(businessId, jobId),
    queryFn: () => getJob(businessId, jobId),
  });

  const paymentMethodsQuery = useQuery({
    queryKey: paymentMethodsQueryKey(businessId),
    queryFn: () => listPaymentMethods(businessId),
  });

  const form = useForm<EditJobInput>({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(editJobSchema),
  });

  useEffect(() => {
    if (!jobQuery.data) return;
    const options = availablePaymentMethodOptions(
      t,
      paymentMethodsQuery.data ?? [],
      has('stripe_payments'),
    );
    form.reset({
      title: jobQuery.data.title,
      serviceAddress: jobQuery.data.service_address ?? '',
      defaultPaymentMethod: paymentMethodOptionValue(
        options,
        jobQuery.data.default_payment_method,
        jobQuery.data.default_payment_method_id,
      ),
      repeats: Boolean(jobQuery.data.frequency),
      recurrence: fromJobRecurrence(jobQuery.data),
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [jobQuery.data, paymentMethodsQuery.data, form]);

  function handleSubmit(data: EditJobInput) {
    const selectedPaymentMethod = availablePaymentMethodOptions(
      t,
      paymentMethodsQuery.data ?? [],
      has('stripe_payments'),
    ).find((o) => o.value === data.defaultPaymentMethod);

    const body: UpdateJobRequest = {
      title: data.title,
      service_address: data.serviceAddress || undefined,
      // '' clears the default; a value sets it. Existing visits are untouched.
      default_payment_method: selectedPaymentMethod?.type ?? null,
      default_payment_method_id: selectedPaymentMethod?.id ?? null,
    };

    if (canRecur && data.repeats && data.recurrence) {
      body.recurrence = toJobRecurrence(data.recurrence);
    } else if (jobQuery.data?.frequency) {
      // Had a series, user switched "Repeats" off (or lost entitlement).
      body.clear_recurrence = true;
    }

    onSave(body);
  }

  const job = jobQuery.data;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="font-semibold text-base truncate">
            {job?.title ?? t.loading}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
            data-testid="modal-close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {jobQuery.isPending ? (
          <p className="px-6 py-8 text-sm text-muted-foreground">{t.loading}</p>
        ) : jobQuery.isError ? (
          <p className="px-6 py-8 text-sm text-destructive">{t.noJobsFound}</p>
        ) : (
          <div className="overflow-y-auto flex-1">
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="px-6 py-5 flex flex-col gap-4">
                {/* Read-only fields */}
                <div className="flex flex-col gap-1.5 text-sm">
                  <span className="text-muted-foreground text-xs">{t.jobCustomer}</span>
                  <span>{job?.customer_name}</span>
                </div>
                <div className="flex flex-col gap-1.5 text-sm">
                  <span className="text-muted-foreground text-xs">{t.jobDate}</span>
                  <span>{job ? formatDate(job.start_date) : '—'}</span>
                </div>

                <Controller
                  control={form.control}
                  name="title"
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="edit-job-title">{t.jobTitle}</Label>
                      <Input
                        {...field}
                        id="edit-job-title"
                        aria-invalid={fieldState.invalid}
                        data-testid="edit-job-title-input"
                      />
                      {fieldState.error && (
                        <p className="text-xs text-destructive">{fieldState.error.message}</p>
                      )}
                    </div>
                  )}
                />

                <Controller
                  control={form.control}
                  name="serviceAddress"
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="edit-job-address">{t.jobAddress}</Label>
                      <Input
                        {...field}
                        id="edit-job-address"
                        aria-invalid={fieldState.invalid}
                        data-testid="edit-job-address-input"
                      />
                    </div>
                  )}
                />

                <Controller
                  control={form.control}
                  name="defaultPaymentMethod"
                  render={({ field }) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="edit-job-default-payment-method">
                        {t.jobDefaultPaymentMethod}
                      </Label>
                      <select
                        {...field}
                        id="edit-job-default-payment-method"
                        data-testid="edit-job-default-payment-method"
                        className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="">
                          {t.jobDefaultPaymentMethodNone}
                        </option>
                        {availablePaymentMethodOptions(
                          t,
                          paymentMethodsQuery.data ?? [],
                          has('stripe_payments'),
                        ).map(
                          (o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                  )}
                />

                {/* Both job forms embed `recurrenceFormFields`; RHF Control is invariant. */}
                {isOwner && canRecur && (
                  <>
                    <RecurrenceFields
                      control={
                        form.control as unknown as Control<RecurrenceFormValues>
                      }
                    />
                    <RecurrencePreview
                      control={
                        form.control as unknown as Control<RecurrenceFormValues>
                      }
                      startDate={job?.start_date ?? ''}
                    />
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-2 px-6 py-4 border-t shrink-0">
                <Button type="button" variant="outline" size="sm" onClick={onClose}>
                  {t.cancel}
                </Button>
                <Button type="submit" size="sm" disabled={isSaving} data-testid="save-job-edit">
                  {t.saveJob}
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
